import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTours } from "@/redux/slices/tourSlice";
import { jwtDecode } from "jwt-decode";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "react-toastify";
import { Link, useLocation } from "react-router-dom";
import LoadingSpinner from "../loading/LoadingSpinner";

const Tour = ({ filters = {}, sortBy }) => {
  const [wishlist, setWishlist] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  var userId = 0;
  if (user) {
    const decoded = jwtDecode(user);
    userId = decoded["sub"];
  }

  const dispatch = useDispatch();
  const {
    tours = [],
    loading,
    error,
    totalCount,
  } = useSelector((state) => state.tour);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Lấy categoryId từ query string
  const queryParams = new URLSearchParams(location.search);
  const categoryIdFromUrl = queryParams.get("categoryId");

  useEffect(() => {
    // Kết hợp filters từ props và categoryId từ URL
    const combinedFilters = {
      ...filters,
      ...(categoryIdFromUrl && { categories: [categoryIdFromUrl] }), // Thêm categoryId vào filters nếu có
      sortBy,
    };
    dispatch(fetchTours({ page: currentPage, pageSize, ...combinedFilters }));
  }, [dispatch, currentPage, filters, sortBy, categoryIdFromUrl]);

  // Handle wishlist
  useEffect(() => {
    if (user) {
      const savedWishlist =
        JSON.parse(localStorage.getItem(`wishlist_${userId}`)) || [];
      setWishlist(savedWishlist);
    }
  }, [userId]);

  const toggleWishlist = (tourId) => {
    if (userId === 0) {
      toast.error("Vui lòng đăng nhập để sử dụng danh sách yêu thích!");
      return;
    }
    let updatedWishlist;

    if (wishlist.includes(tourId)) {
      updatedWishlist = wishlist.filter((id) => id !== tourId);
      toast.info("Đã xóa khỏi danh sách yêu thích");
    } else {
      updatedWishlist = [...wishlist, tourId];
      toast.success("Đã thêm vào danh sách yêu thích");
    }

    setWishlist(updatedWishlist);
    localStorage.setItem(`wishlist_${userId}`, JSON.stringify(updatedWishlist));
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  // Hàm loại bỏ thẻ HTML và thêm xuống dòng
  const stripHtml = (html) => {
    return html
      .replace(/<[^>]+>/g, "\n") // Thay thẻ HTML bằng \n
      .replace(/\n+/g, "\n") // Loại bỏ nhiều \n liên tiếp thành một
      .trim(); // Xóa khoảng trắng thừa
  };

  return (
    <>
      {loading && (
        <p className="text-primary">
          <LoadingSpinner
            type="circle"
            color="#3498db"
            size={30}
            text="Đang tải dữ liệu..."
          />
        </p>
      )}
      {error && <p className="text-danger">{error}</p>}

      {!loading && !error && tours.length > 0 ? (
        <div className="row">
          {tours.map((tour) => {
            const plainDescription = stripHtml(tour.description);

            // Tìm discount đang active và có phần trăm giảm cao nhất
            const activeDiscount = tour.discounts
              ?.filter((d) => d.isActive)
              .sort((a, b) => b.discountPercentage - a.discountPercentage)[0];
            const originalPrice = tour.price || 0;
            const discountedPrice = activeDiscount
              ? originalPrice * (1 - activeDiscount.discountPercentage / 100)
              : originalPrice;

            return (
              <div key={tour.tourId} className="col-xxl-4 col-md-6 d-flex">
                <div className="place-item mb-4 flex-fill">
                  <div className="place-img">
                    <Slider
                      {...settings}
                      className="img-slider image-slide owl-carousel nav-center"
                    >
                      {tour.imageUrls?.length ? (
                        tour.imageUrls.map((imageUrl, index) => (
                          <div key={index} className="slide-images">
                            <Link to={`/tour-detail/${tour.tourId}`}>
                              <img
                                src={imageUrl}
                                className="img-fluid"
                                alt={tour.tourName}
                                style={{ height: "200px", objectFit: "cover" }}
                              />
                            </Link>
                          </div>
                        ))
                      ) : (
                        <div className="slide-images">
                          <Link to={`/tour-detail/${tour.tourId}`}>
                            <img
                              src="assets/img/tours/default.jpg"
                              className="img-fluid"
                              alt="Tour Default"
                            />
                          </Link>
                        </div>
                      )}
                    </Slider>

                    <div className="fav-item">
                      <a
                        href="#"
                        className={`fav-icon ${
                          wishlist.includes(tour.tourId) ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleWishlist(tour.tourId);
                        }}
                      >
                        <i className="isax isax-heart5"></i>
                      </a>
                      <span className="badge bg-info d-inline-flex align-items-center">
                        <i className="isax isax-ranking me-1"></i>Trending
                      </span>
                    </div>
                  </div>

                  <div className="place-content">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <div className="d-flex flex-wrap align-items-center">
                        <span className="me-1">
                          <i className="ti ti-receipt text-primary"></i>
                        </span>
                        <p className="fs-14 text-gray-9">
                          <Link to={`/category/${tour.categoryId}`}>
                            {tour.categoryName}
                          </Link>
                        </p>
                      </div>
                      <span className="d-inline-block border vertical-splits">
                        <span className="bglight text-light d-flex align-items-center justify-content-center"></span>
                      </span>
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="badge badge-warning badge-xs text-gray-9 fs-13 fw-medium me-1">
                          {tour.averageRating !== null
                            ? tour.averageRating.toFixed(1)
                            : "0"}
                        </span>
                        <p className="fs-14">({tour.reviewCount} đánh giá)</p>
                      </div>
                    </div>
                    <h5 className="mb-1 text-truncate">
                      <Link to={`/tour-detail/${tour.tourId}`}>
                        {tour.tourName}
                      </Link>
                    </h5>
                    <p
                      className="d-flex align-items-center mb-3"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      <i className="isax isax-document-text me-2"></i>
                      {plainDescription.length > 80
                        ? `${plainDescription.substring(0, 80)}...`
                        : plainDescription}
                    </p>
                    <div className="mb-3">
                      <h6 className="d-flex align-items-center text-gray-6 fs-14 fw-normal">
                        Giá{" "}
                        <span className="ms-1 fs-18 fw-semibold text-primary">
                          {discountedPrice.toLocaleString("vi-VN", {
                            style: "currency",
                            currency: "VND",
                          })}
                        </span>
                        {activeDiscount && (
                          <span className="ms-1 fs-18 fw-semibold text-gray-3 text-decoration-line-through">
                            {originalPrice.toLocaleString("vi-VN", {
                              style: "currency",
                              currency: "VND",
                            })}
                          </span>
                        )}
                      </h6>
                    </div>
                    <div className="d-flex align-items-center justify-content-between border-top pt-3">
                      <div className="d-flex flex-wrap align-items-center me-2">
                        <span className="me-1">
                          <i className="isax isax-calendar-tick text-gray-6"></i>
                        </span>
                        <p className="fs-14 text-gray-9">
                          Số ngày: {tour.numberDay}
                        </p>
                      </div>
                      <span className="d-inline-block border vertical-splits">
                        <span className="bglight text-light d-flex align-items-center justify-content-center"></span>
                      </span>
                      <div className="ms-2 d-flex align-items-center">
                        <p className="fs-14 text-gray-9 mb-0 text-truncate d-flex align-items-center">
                          <i className="isax isax-profile-2user me-1"></i>
                          {tour.maxParticipants} du khách
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !loading &&
        !error && <p className="text-muted">Không có tour nào để hiển thị.</p>
      )}

      {totalPages > 1 && (
        <nav className="pagination-nav">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                style={{ height: "100%" }}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <li
                  key={pageNumber}
                  className={`page-item ${
                    currentPage === pageNumber ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                </li>
              )
            )}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                style={{ height: "100%" }}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default Tour;
